import React, { useEffect } from "react";
import { Link, useParams, Navigate } from "react-router-dom";
import { motion } from "framer-motion";
import { ArrowLeft, Clock, Phone, Calendar, Share2 } from "lucide-react";
import Navbar from "@/components/Navbar";
import Footer from "@/components/Footer";
import ArticleRenderer from "@/components/ArticleRenderer";
import { getArticleBySlug, getRelatedArticles, CATEGORIES } from "@/lib/articles";
import { SITE } from "@/lib/site";

const formatDate = (iso) => {
  try { return new Date(iso).toLocaleDateString("fr-FR", { day: "numeric", month: "long", year: "numeric" }); }
  catch { return iso; }
};

export const BlogPost = () => {
  const { slug } = useParams();
  const article = getArticleBySlug(slug);

  useEffect(() => {
    if (!article) return;
    document.title = article.metaTitle;
    const setMeta = (name, content) => {
      let el = document.querySelector(`meta[name="${name}"]`);
      if (!el) { el = document.createElement("meta"); el.name = name; document.head.appendChild(el); }
      el.content = content;
    };
    setMeta("description", article.metaDescription);

    let canonical = document.querySelector('link[rel="canonical"]');
    if (!canonical) { canonical = document.createElement("link"); canonical.rel = "canonical"; document.head.appendChild(canonical); }
    canonical.href = `https://drones3x.fr/blog/${article.slug}`;

    // BlogPosting JSON-LD
    let scriptEl = document.getElementById("blogpost-jsonld");
    if (!scriptEl) {
      scriptEl = document.createElement("script");
      scriptEl.type = "application/ld+json";
      scriptEl.id = "blogpost-jsonld";
      document.head.appendChild(scriptEl);
    }
    scriptEl.text = JSON.stringify({
      "@context": "https://schema.org",
      "@type": "BlogPosting",
      "headline": article.title,
      "description": article.metaDescription,
      "image": article.cover,
      "datePublished": article.publishedAt,
      "dateModified": article.publishedAt,
      "author": { "@type": "Organization", "name": "drones3x", "url": "https://drones3x.fr/" },
      "publisher": {
        "@type": "Organization",
        "name": "drones3x",
        "logo": { "@type": "ImageObject", "url": "https://drones3x.fr/logo.png" },
      },
      "mainEntityOfPage": { "@type": "WebPage", "@id": `https://drones3x.fr/blog/${article.slug}` },
      "keywords": article.keyword,
      "articleSection": "Spectacle de drones",
      "inLanguage": "fr-FR",
    });

    // Breadcrumb JSON-LD for SERP display
    let breadcrumbEl = document.getElementById("blog-breadcrumb-jsonld");
    if (!breadcrumbEl) {
      breadcrumbEl = document.createElement("script");
      breadcrumbEl.type = "application/ld+json";
      breadcrumbEl.id = "blog-breadcrumb-jsonld";
      document.head.appendChild(breadcrumbEl);
    }
    breadcrumbEl.text = JSON.stringify({
      "@context": "https://schema.org",
      "@type": "BreadcrumbList",
      "itemListElement": [
        { "@type": "ListItem", "position": 1, "name": "Accueil", "item": "https://drones3x.fr/" },
        { "@type": "ListItem", "position": 2, "name": "Blog", "item": "https://drones3x.fr/blog" },
        { "@type": "ListItem", "position": 3, "name": article.title, "item": `https://drones3x.fr/blog/${article.slug}` },
      ],
    });

    window.scrollTo(0, 0);

    return () => {
      const ldEl = document.getElementById("blogpost-jsonld");
      if (ldEl) ldEl.remove();
      const bcEl = document.getElementById("blog-breadcrumb-jsonld");
      if (bcEl) bcEl.remove();
    };
  }, [article]);

  if (!article) return <Navigate to="/blog" replace />;

  const related = getRelatedArticles(article.slug, 3);
  const cat = CATEGORIES[article.category];

  const share = async () => {
    const url = `https://drones3x.fr/blog/${article.slug}`;
    if (navigator.share) {
      try { await navigator.share({ title: article.title, url }); } catch {}
    } else {
      navigator.clipboard?.writeText(url);
    }
  };

  return (
    <div className="App">
      <Navbar />
      <main className="pt-32 pb-28">
        <article className="relative">
          <div className="absolute inset-0 top-0 h-[60vh] -z-10">
            <img src={article.cover} alt="" className="h-full w-full object-cover opacity-25" />
            <div className="absolute inset-0 bg-gradient-to-b from-transparent via-[#030305]/60 to-[#030305]" />
          </div>

          <div className="mx-auto max-w-3xl px-6">
            {/* Breadcrumb */}
            <Link
              to="/blog"
              className="inline-flex items-center gap-2 text-sm text-white/60 hover:text-white transition"
            >
              <ArrowLeft size={14} /> Retour au blog
            </Link>

            {/* Header */}
            <div className="mt-8">
              <span
                className="inline-flex items-center gap-1.5 rounded-full px-3 py-1 text-[10px] uppercase tracking-[0.2em] font-bold"
                style={{ background: cat.color, color: "#020205" }}
              >
                {cat.label}
              </span>
              <h1 className="font-display mt-6 text-3xl md:text-5xl lg:text-[56px] leading-tight font-light tracking-tight">
                {article.title}
              </h1>
              <div className="mt-8 flex flex-wrap items-center gap-5 text-sm text-white/55">
                <span className="inline-flex items-center gap-2"><Calendar size={14} /> {formatDate(article.publishedAt)}</span>
                <span className="inline-flex items-center gap-2"><Clock size={14} /> Lecture {article.readingMinutes} min</span>
                <button onClick={share} className="inline-flex items-center gap-2 hover:text-white transition">
                  <Share2 size={14} /> Partager
                </button>
              </div>
            </div>

            {/* Cover */}
            <motion.div
              initial={{ opacity: 0, scale: 0.97 }}
              animate={{ opacity: 1, scale: 1 }}
              transition={{ duration: 0.9 }}
              className="relative mt-12 aspect-[16/9] rounded-3xl overflow-hidden"
            >
              <img src={article.cover} alt={article.title} className="absolute inset-0 h-full w-full object-cover" />
              <div className="absolute inset-0 bg-gradient-to-t from-[#030305]/40 to-transparent" />
            </motion.div>

            {/* Content */}
            <div className="mt-14">
              <ArticleRenderer content={article.content} />
            </div>

            {/* CTA in-article */}
            <div className="mt-16 rounded-3xl glass-strong p-8 md:p-10">
              <h3 className="font-display text-2xl md:text-3xl font-semibold">
                Un projet en tête ?
              </h3>
              <p className="mt-3 text-white/65">
                Devis sous 24h, transparence totale. Notre équipe basée à Enghien-les-Bains
                vous accompagne en France, en Europe et au Maroc.
              </p>
              <div className="mt-6 flex flex-wrap gap-4">
                <Link to="/#contact" className="inline-flex items-center gap-2 rounded-full bg-[#007AFF] px-6 py-3 text-sm font-semibold hover:shadow-[0_0_32px_rgba(0,122,255,0.55)] transition">
                  Demander un devis →
                </Link>
                <a href={SITE.phoneLink} className="inline-flex items-center gap-2 rounded-full glass px-6 py-3 text-sm font-medium hover:bg-white/10 transition">
                  <Phone size={14} /> {SITE.phoneDisplay}
                </a>
              </div>
            </div>

            {/* Related */}
            {related.length > 0 && (
              <div className="mt-20">
                <h3 className="font-display text-2xl md:text-3xl font-semibold mb-8">
                  À lire aussi
                </h3>
                <div className="grid gap-5 md:grid-cols-3">
                  {related.map((r) => {
                    const rc = CATEGORIES[r.category];
                    return (
                      <Link
                        key={r.slug}
                        to={`/blog/${r.slug}`}
                        data-testid={`blog-related-${r.slug}`}
                        className="group block rounded-2xl overflow-hidden glass-strong"
                      >
                        <div className="relative aspect-video overflow-hidden">
                          <img src={r.cover} alt={r.title} className="absolute inset-0 h-full w-full object-cover opacity-60 group-hover:opacity-90 transition" />
                          <span
                            className="absolute top-3 left-3 inline-flex rounded-full px-2.5 py-0.5 text-[9px] uppercase tracking-[0.2em] font-bold"
                            style={{ background: rc.color, color: "#020205" }}
                          >
                            {rc.label}
                          </span>
                        </div>
                        <div className="p-5">
                          <h4 className="font-display text-base font-semibold leading-tight group-hover:text-[#007AFF] transition">
                            {r.title}
                          </h4>
                        </div>
                      </Link>
                    );
                  })}
                </div>
              </div>
            )}
          </div>
        </article>
      </main>
      <Footer />
    </div>
  );
};

export default BlogPost;
